import { Spin } from "antd";
import dayjs from "dayjs";
import { DollarSign, Pin } from "lucide-react";
import { FC } from "react";
import { ContainerInfoCard } from "~/components/common/container-info";
import { useGetNotes } from "~/services/note";
import { capitalize, formatDateTime, getClientName } from "~/utils";
import { SortOrder } from "~/utils/constants";
import { getPaymentBadge } from "../booking-utils";
import { BookingTab, useDetailBookingStore } from "../detail-booking.store";

type Props = {};

export const Overview: FC<Props> = () => {
  const { booking, setActiveTab } = useDetailBookingStore();

  // Fetch notes using useGetNotes
  const { data: notesData, isLoading: isNotesLoading } = useGetNotes({
    jobTicketId: booking?.id,
    sortByCreatedAt: SortOrder.DESC,
  });

  if (!booking) return null;

  // Calculate the latest service date from all service tickets
  const getLatestServiceDate = () => {
    if (!booking.serviceTicket || booking.serviceTicket.length === 0) {
      return null;
    }

    return booking.serviceTicket.reduce((latest, ticket) => {
      const ticketDate = dayjs(ticket.serviceDatetime);
      return latest === null || ticketDate > latest ? ticketDate : latest;
    }, null as dayjs.Dayjs | null);
  };

  // calculate the first service date from all service tickets
  const getFirstServiceDate = () => {
    if (!booking.serviceTicket || booking.serviceTicket.length === 0) {
    return null;
    }
    return booking.serviceTicket.reduce((earliest, ticket) => {
      const ticketDate = dayjs(ticket.serviceDatetime);
      return earliest === null || ticketDate < earliest ? ticketDate : earliest;
    }, null as dayjs.Dayjs | null);
  };
  
  // Deduplicate inventory items by their id
  const uniqueInventoryItems = Array.from(
    new Map(
      booking.serviceTicket
        .flatMap((ticket) => ticket.inventories || [])
        .map((item) => [item.id, item])
    ).values()
  );

  const firstServiceDate = getFirstServiceDate();
  const latestServiceDate = getLatestServiceDate();
  
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="font-medium mb-4">Booking Information</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500">Booking ID</div>
                <div>{booking.referenceNumber}</div>
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-500">Project</div>
              <div>{booking.project?.name}</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500">Start Date</div>
                <div>   {firstServiceDate  ? formatDateTime(firstServiceDate): "No services scheduled"}
    </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Last Service Date</div>
                <div>
                  {latestServiceDate
                    ? formatDateTime(latestServiceDate)
                    : "No services scheduled"}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500">Created</div>
                <div>{new Date(booking.createdAt).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Last Updated</div>
                <div>{new Date(booking.updatedAt).toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-medium mb-4">Client Information</h3>
          {booking.tempClientData ? (
            /* ESTIMATE MODE - Show temp client data */
            <div className="space-y-3">
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
                <div className="text-sm text-yellow-800 font-medium">
                  📋 Estimate - Temporary Client Data
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Contact</div>
                <div>{booking.tempClientData.name}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Phone</div>
                  <div>{booking.tempClientData.phone || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Email</div>
                  <div>{booking.tempClientData.email}</div>
                </div>
              </div>
            </div>
          ) : (
            /* NORMAL MODE - Show real client data */
            <div className="space-y-3">
              <div>
                <div className="text-sm text-gray-500">Contact</div>
                <div>{getClientName(booking.client)}</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Phone</div>
                  <div>{booking.client?.contactPhone || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Email</div>
                  <div>{booking.client?.email || 'N/A'}</div>
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 mb-2">Addresses</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {booking?.client?.shippingAddresses?.map((address, index) => (
                    <div key={index} className="border rounded p-2 bg-gray-50">
                      <div className="font-medium">{address.name}</div>
                      <div className="text-sm">{address.address}</div>
                      <div className="text-sm">
                        {address.city}, {address.state} {address.zip}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Services Summary */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium">Services</h3>
            <button
              onClick={() => setActiveTab(BookingTab.SERVICES)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {booking.serviceTicket
              .sort((a, b) => {
                const dateA = new Date(a.serviceDatetime);
                const dateB = new Date(b.serviceDatetime);
                return dateA.getTime() - dateB.getTime();
              })
              .slice(0, 6)
              .map((service) => (
                <ContainerInfoCard
                  key={service.id}
                  serviceTicket={service}
                  variant="summary"
                  className="border rounded-lg"
                  bookingId={booking.id}
                />
              ))}
            {booking.serviceTicket.length > 6 && (
              <button
                onClick={() => setActiveTab(BookingTab.SERVICES)}
                className="w-full text-sm text-blue-600 hover:text-blue-800 py-2 border border-blue-200 rounded-lg hover:bg-blue-50"
              >
                Click to view {booking.serviceTicket.length - 6} more service{booking.serviceTicket.length - 6 !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>

        {/* Inventory Summary */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium">Inventory</h3>
            <button
              onClick={() => setActiveTab(BookingTab.INVENTORY)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              View All
            </button>
          </div>
          <div className="border rounded-lg">
            {uniqueInventoryItems.length > 0 ? (
              <>
                <div className="divide-y">
                  {uniqueInventoryItems.slice(0, 15).map((item) => (
                    <div key={item.id} className="p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{item.title}</div>
                          <div className="text-sm text-gray-500">
                            {item.inventoryRef}
                          </div>
                        </div>
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                          {capitalize(item.status)}
                        </span>
                      </div>
                      <div className="flex items-center mt-1 text-sm">
                        <span>
                          {item.width}" x {item.height}" x {item.length}"
                        </span>
                        <span className="mx-1">•</span>
                        <span>{item.location?.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {uniqueInventoryItems.length > 15 && (
                  <button
                    onClick={() => setActiveTab(BookingTab.INVENTORY)}
                    className="w-full text-sm text-blue-600 hover:text-blue-800 py-2 border-t hover:bg-blue-50"
                  >
                    Click to view {uniqueInventoryItems.length - 15} more item{uniqueInventoryItems.length - 15 !== 1 ? 's' : ''}
                  </button>
                )}
              </>
            ) : (
              <div className="p-4 text-center text-gray-500">
                No inventory items
              </div>
            )}
          </div>
        </div>

        {/* Financial Summary */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium">Financial</h3>
            <button
              onClick={() => setActiveTab(BookingTab.BILLING)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              View Details
            </button>
          </div>
          <div className="border rounded-lg p-4">
            {booking.invoiceConfig?.grandTotal ? (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Estimate Total</span>
                  <span className="text-xl font-semibold">
                    ${booking.invoiceConfig.grandTotal.toFixed(2)}
                  </span>
                </div>
                {booking.invoiceConfig.requestedDeposit > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Requested Deposit</span>
                    <span className="font-medium">
                      ${booking.invoiceConfig.requestedDeposit.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-4">
                No estimate available
              </div>
            )}

            {booking.payment && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-500">
                    Invoice #{booking.payment?.invoiceId.split("-")[2]}
                  </span>
                  {getPaymentBadge(booking.payment.status)}
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Total</span>
                    <span className="font-medium">
                      ${booking.payment.total.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Paid</span>
                    <span className="text-green-600">
                      ${booking.payment.paid.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Due</span>
                    <span className="text-red-600">
                      ${booking.payment.due.toFixed(2)}
                    </span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between font-medium">
                      <span>Balance</span>
                      <span
                        className={
                          booking.payment.due > 0
                            ? "text-red-600"
                            : "text-green-600"
                        }
                      >
                        ${booking.payment.due.toFixed(2)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Due on{" "}
                      {new Date(booking.payment.dueDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {booking.payment.due > 0 && (
                  <button className="w-full mt-4 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-1">
                    <DollarSign size={16} />
                    <span>Process Payment</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Notes */}
      <div className="mt-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-medium">Recent Notes</h3>
          <button
            onClick={() => setActiveTab(BookingTab.NOTES)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            View All
          </button>
        </div>
        <Spin spinning={isNotesLoading}>
          <div className="border rounded-lg p-4">
            {notesData?.items?.map((note) => (
              <div
                key={note.id}
                className={`mb-3 p-3 border-b last:border-0 last:mb-0 last:pb-0 ${
                  note.isPinned ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="font-medium flex items-center">
                    {note.isPinned && (
                      <Pin size={16} className="mr-1 text-blue-600" />
                    )}
                    {note.author?.firstName} {note.author?.lastName}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(note.createdAt).toLocaleString()}
                  </div>
                </div>
                <div className="mt-1">{note.content}</div>
              </div>
            ))}
            {!notesData?.items?.length && !isNotesLoading && (
              <div className="text-gray-500 text-center">
                No notes available
              </div>
            )}

            <div className="flex space-x-2">
              <input
                type="text"
                className="flex-1 border rounded-lg px-3 py-2"
                placeholder="Add a note..."
                disabled
              />
              <button
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                onClick={() => setActiveTab(BookingTab.NOTES)}
              >
                Add Note
              </button>
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
};
